import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { PolicyService } from '@modules/policy/policy.service';
import { JsonFormFetcherService } from './../../../services/shared/json-form-fetcher.service';
import { FormTabService } from './../../../../../../../projects/form-tab/src/lib/form-tab.service';
import { PolicyDataService } from './../../../services/shared/policy-data.service';
import { DynamicDropdownService } from './../../../services/shared/dynamic.dropdown.service';
import { TreeService } from './../../../services/location/tree.service';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';

import { ActivatedRoute } from '@angular/router';
import { FormService } from '@modules/policy/services/shared/form.service';
import { FormSaveHelper } from '@modules/policy/services/shared/save';
import { SaveService } from '@modules/policy/services/shared/save.service';
import LocationTabs from '@shared/models/location.model';
import { PolicyState } from '@shared/models/state.model';
import { combineLatest, isObservable, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { CoverageTableComponent } from '../coverage-table/coverage-table.component';

@Component({
  selector: 'app-nested-subtab-form',
  templateUrl: './nested-subtab-form.component.html',
  styleUrls: ['./nested-subtab-form.component.scss']
})
export class NestedSubtabFormComponent implements OnInit {
  @Input() subtab: string;
  @Input() tab: string;
  @Input() type: string;
  @Output() newFormEvent = new EventEmitter<FormGroup>();

  form: FormGroup;
  formJson: any;
  policyId: string;
  formId: number;
  locationId: string;
  subtabs: [];
  riskStates;
  dataSource = new MatTreeNestedDataSource<LocationTabs>();
  $matTreeSubscription;
  $subjects;
  states: PolicyState;
  listName: string;
  readOnlyPolicy: boolean;
  private coverageData: any;
  private additionalCoverageDependencies: { [s: string]: AbstractControl } = {};
  @ViewChild("coverageTableComponent", { static: false }) coverageTableComponent: CoverageTableComponent;

  constructor(
    private route: ActivatedRoute,
    private saveService: SaveService,
    private FormHelper: FormService,
    private PolicyService: PolicyService,
    private JsonFormFetcherService: JsonFormFetcherService,
    private FormTabService: FormTabService,
    private PolicyDataService: PolicyDataService,
    private SaveHelper: FormSaveHelper,
    private DynamicDropdownService: DynamicDropdownService,
    private snackbar: MatSnackBar,
    private TreeService: TreeService
  ) { }

  saveBeforeDeactivate() {
    if (!this.form) {
      return of(null).toPromise();
    }

    const persistedValid = this.getPersistedNodeState();

    // Only a real data edit may drive validity from the live Angular form.
    // `touched` is NOT an edit: simply visiting a node (focus/blur, conditional or
    // underwriting validators firing on load) can flip a completed form to a false
    // INVALID. Persisting or displaying that state is what makes the location tab
    // show a phantom warning and blocks Get Quote. See CPROD-2815 / CPROD-2786.
    if (this.form.dirty) {
      return this.save(true).toPromise().then((res) => {
        this.PolicyService.updateTouchedNodeStates(res);
        this.restoreNavigationStub(this.form.valid);
      });
    }

    // Visited but not edited. Promote a genuinely-valid form to VALID (recovers
    // stale-false nav state), but never downgrade: a false INVALID on a pristine
    // form must defer to the persisted (DB-backed) state.
    if (this.form.valid && persistedValid !== true) {
      const cascadingIds = this.buildCascadingNavigationState(true);
      return this.SaveHelper.syncNavigationState(this.policyId, cascadingIds).then(() => {
        this.restoreNavigationStub(true);
        return null;
      });
    }

    const leafValid = this.form.valid || persistedValid !== false;
    this.restoreNavigationStub(leafValid);
    return of(null).toPromise();
  }

  private getPersistedNodeState(): boolean | undefined {
    return this.TreeService.getNodeState(this.dataSource.data || [], Number(this.formId));
  }

  private buildCascadingNavigationState(leafValid: boolean): object {
    const formId = Number(this.formId);
    const locationId = Number(this.locationId);
    const tree = this.dataSource.data || [];
    const cascadingIds: any = {
      nodeState: [formId, leafValid]
    };

    if (this.tab !== 'location' || tree.length === 0) {
      return cascadingIds;
    }

    this.TreeService.updateParentState(tree, formId, leafValid);

    const riskId = this.TreeService.getParent(tree, formId);
    const locationExists = tree.some(loc => loc.id == locationId);
    const riskExists = !!(riskId && tree.some(loc =>
      loc.id == locationId && loc.children && loc.children.some(child => child.id == riskId)
    ));

    if (riskExists) {
      const riskValid = this.TreeService.isRiskValid(riskId, locationId);
      cascadingIds.riskState = [riskId, riskValid];
      this.TreeService.updateParentState(tree, riskId, riskValid);
    }
    if (locationExists) {
      const locationValid = this.TreeService.isLocationValid(locationId);
      cascadingIds.locationState = [locationId, locationValid];
      this.TreeService.updateParentState(tree, locationId, locationValid);
    }
    if (this.states && this.states['location'] && this.states['location'].id != null) {
      const tabForm = this.PolicyService.getTabForms().location;
      const tabValid = leafValid && (!tabForm || tabForm.status === 'DISABLED' || tabForm.valid);
      cascadingIds.tabState = [this.states['location'].id, tabValid];
    }
    return cascadingIds;
  }

  private restoreNavigationStub(leafValid: boolean) {
    const formId = Number(this.formId);
    this.FormTabService.clearRuleBasedValidations(formId);
    this.PolicyService.restoreValidityStub(formId, leafValid);
    const stub = this.PolicyService.getForm(formId);
    this.TreeService.appendFormToData(this.dataSource.data || [], formId, stub);
    this.TreeService.updateParentState(this.dataSource.data || [], formId, leafValid);
  }

  newForm(form: FormGroup) {
    this.newFormEvent.emit(form);
  }

  ngOnInit() {
    this.matTreeSubscription();
    this.policyId = this.PolicyDataService.getPolicyID();
    const stateSubject = this.PolicyDataService.stateSubject;
    const configSubject = this.JsonFormFetcherService.tabsConfigSubject;
    this.$subjects = combineLatest([stateSubject, configSubject, this.route.params]).pipe(
      map(res => {
        const [states, config, params] = res;
        return {
          params,
          states,
          config
        }
      })
    ).subscribe(data => {
      const { states, config, params } = data;
      const { formId, locationId } = params;
      this.states = states;
      this.formId = formId;
      this.locationId = locationId;
      const subtabConfig = config.tabs.find(curTab => curTab.tabKey === this.tab);
      const { shouldCache } = subtabConfig;
      this.listName = this.PolicyDataService.getListName(this.tab, this.subtab, config.tabs);
      if (this.tab === 'policy') {
        this.subtabs = subtabConfig.subTabs;
      }
      else if (this.tab === 'location') {
        for (let subtab of subtabConfig.lists[0].lists) {
          if (subtab.listName === this.type) {
            this.subtabs = subtab.subTabs;
            break;
          }

          if (subtab.lists != null) {
            for (let subtabList of subtab.lists) {
              if (subtabList.listName === this.type) {
                this.subtabs = subtabList.subTabs.map((listSubTab) => {
                  return {
                    isList: true,
                    ...listSubTab
                  }
                });
                break;
              }
            }
          }

          if (this.subtabs != null) {
            break;
          }
        }
      }
      
      if (this.PolicyService.getForm(this.formId)) {
        this.handleForms(shouldCache);
      }
    });
  }

  save(routeChange?: boolean) {
    if (this.form.disabled) return
    const cascadingIds = this.buildCascadingNavigationState(this.form.valid);

    const saveObs: Observable<any> = this.SaveHelper.save(
      this.form,
      cascadingIds,
      this.subtab,
      this.saveService,
      this.policyId
    );
    if (routeChange) {
      return saveObs;
    }
    if (this.coverageTableComponent != null) {
      this.coverageTableComponent.save();
    }
    saveObs.toPromise().then((res) => {
      this.showSaveSnackbar();
      if (res != null && res.data.parent != null) {
        this.TreeService.setParentNodeName(res.data)
        this.PolicyDataService.getPolicyStates(this.PolicyDataService.getPolicyID()).toPromise().then((res) => {
          this.PolicyDataService.updateStates(res);
        });
      }
    });
  }

  updateNavigationTree() {
    this.PolicyDataService.getPolicyStates(this.policyId).subscribe(tree => {
      this.PolicyDataService.updateStates(tree);
    })
  }

  showSaveSnackbar() {
    this.snackbar.open('Saved', null, {
      duration: 2000,
    });
    this.updateNavigationTree();
  }

  handleForms(shouldCache: boolean) {
    const result = this.FormHelper.initForm({
      subtab: this.subtab,
      formid: this.formId,
      subTabList: this.subtabs,
      policyID: this.policyId,
      shouldCache
    });
    if (typeof result === 'number') {
      this.displayForm(result);
    }
    if (isObservable<any>(result)) {
      this.getForm(result);
    }
  }

  getForm(obs: Observable<any>) {
    obs.subscribe((result) => {
      this.form = result.form;
      this.formJson = result.orderedFormJson;
      this.FormHelper.setForm({
        formid: this.formId,
        form: this.form,
        orderedFormJson: this.formJson
      });
      this.afterFormInit();
    }, (err: any) => console.error(err));
  }

  displayForm(formId: number) {
    this.form = this.PolicyService.getForm(formId);
    this.formJson = this.PolicyService.getFormJson(formId);
    this.afterFormInit();
  }

  afterFormInit() {
    this.handleReadOnlyForm()
    if (Array.isArray(this.formJson)) {
      this.formJson.forEach((formSection) => {
        if (formSection.customSection != null) {
          this.initCoverageTable();
          return;
        }
      });
    }
    this.newForm(this.form);
    // Load-time validators / disabling rules (which call setValue) can mark the form
    // dirty & touched even though the user never edited it. Reset so a mere visit is
    // not mistaken for an edit in saveBeforeDeactivate (CPROD-2815).
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.TreeService.appendFormToData(this.dataSource.data, this.formId, this.form);
  }

  initCoverageTable() {
    const values = this.PolicyDataService.getValueFromCache(Number(this.formId));
    if (values.PrimaryCoverages != null && values.PrimaryCoverages.length > 0) {
      this.coverageData = {
        ...values.PrimaryCoverages,
        ...this.coverageData
      }
    }
    if (values.AdditionalCoverages != null && values.AdditionalCoverages.length > 0) {
      this.coverageData = {
        ...values.AdditionalCoverages,
        ...this.coverageData
      }
      // TODO: We VERY MUCH need a way to genericize this - this is not a pattern that we've come across before and
      // won't work cleanly with anything else. People gotta know what they're asking for.
      // I also hate that I've got a reference to "ItemDetails" here. Blech.
      switch (this.subtab) {
        case 'grainDetails':
          break;
        default:
          const itemDetails = this.form.get('ItemDetails');
          if (itemDetails == null) break;
          this.additionalCoverageDependencies = {
            ScheduleItemDeductibleCd: itemDetails.get('ScheduleItemDeductibleCd'),
            ScheduleItemValueAmt: itemDetails.get('ScheduleItemValueAmt')
          }
      }
    }
  }

  initForm(form: FormGroup) { 
    this.form.setControl('CoverageTable', form)
  }

  matTreeSubscription() {
    this.$matTreeSubscription = this.TreeService.dataSourceSubject.subscribe(data => {
      this.dataSource.data = null;
      this.dataSource.data = data;
    });
  }

  handleReadOnlyForm() {
    this.PolicyDataService.policyReadOnly.subscribe(readOnly => {
      this.readOnlyPolicy = readOnly
      if (readOnly) this.form.disable()
    })
  }

  ngOnDestroy() {
    if (this.$subjects) {
      this.$subjects.unsubscribe();
    }
    if (this.$matTreeSubscription) {
      this.$matTreeSubscription.unsubscribe();
    }
  }
}
